import { ApiService } from "./apiService";
import { routes } from "./paths";
import {
  BlogCategoryDto,
  BlogCategoryDetailDto,
  BlogPostDto,
  BlogPostDetailDto,
  CreateBlogCategoryCommand,
  UpdateBlogCategoryCommand,
  CreateBlogPostCommand,
  UpdateBlogPostCommand,
  BlogCategoryCreated,
  BlogCategoryUpdated,
  BlogCategoryDeleted,
  BlogPostCreated,
  BlogPostUpdated,
  BlogPostDeleted,
} from "../types/app/blogTypes";

export const getBlogCategories = async (
  includePostCount: boolean = false
): Promise<BlogCategoryDto[]> => {
  const url = `${routes.GET_BLOG_CATEGORIES}?includePostCount=${includePostCount}`;
  const response: any = await ApiService.request({
    url,
    http_method: "GET",
  });
  
  if (response && response.data) {
    return Array.isArray(response.data) ? response.data : [];
  }
  
  return Array.isArray(response) ? response : [];
};

export const getBlogCategoryById = (
  id: string
): Promise<BlogCategoryDetailDto> => {
  const url = `${routes.GET_BLOG_CATEGORY_BY_ID}/${id}`;
  return ApiService.request({
    url,
    http_method: "GET",
  });
};

export const getBlogCategoryBySlug = async (
  slug: string
): Promise<BlogCategoryDto> => {
  const url = `${routes.GET_BLOG_CATEGORY_BY_SLUG}/${slug}`;
  const response: any = await ApiService.request({
    url,
    http_method: "GET",
  });
  
  // ApiService.request returns { data: actualResponse }
  return response?.data;
};

export const createBlogCategory = (
  data: CreateBlogCategoryCommand
): Promise<BlogCategoryCreated> => {
  const url = routes.CREATE_BLOG_CATEGORY;
  return ApiService.request({
    url,
    http_method: "POST",
    data,
  });
};

export const updateBlogCategory = (
  id: string,
  data: UpdateBlogCategoryCommand
): Promise<BlogCategoryUpdated> => {
  const url = `${routes.UPDATE_BLOG_CATEGORY}/${id}`;
  return ApiService.request({
    url,
    http_method: "PUT",
    data,
  });
};

export const deleteBlogCategory = (
  id: string
): Promise<BlogCategoryDeleted> => {
  const url = `${routes.DELETE_BLOG_CATEGORY}/${id}`;
  return ApiService.request({
    url,
    http_method: "DELETE",
  });
};

export const getBlogPosts = async (
  pageNumber: number = 1,
  pageSize: number = 10,
  status?: string
): Promise<{
  pageNumber: number;
  pageSize: number;
  totalNumberOfPages: number;
  totalNumberOfRecords: number;
  nextPageUrl: string | null;
  prevPageUrl: string | null;
  results: BlogPostDto[];
}> => {
  let url = `${routes.GET_BLOG_POSTS}?PageNumber=${pageNumber}&PageSize=${pageSize}`;
  
  if (status) {
    url += `&Status=${status}`;
  }
  
  const response: any = await ApiService.request({
    url,
    http_method: "GET",
  });
  
  return response.data;
};

export const getBlogPostBySlug = async (
  slug: string
): Promise<BlogPostDetailDto> => {
  const url = `${routes.GET_BLOG_POST_BY_SLUG}/${slug}`;
  const response: any = await ApiService.request({
    url,
    http_method: "GET",
  });
  
  // ApiService.request returns { data: actualResponse }
  return response.data;
};

export const createBlogPost = (
  data: CreateBlogPostCommand
): Promise<BlogPostCreated> => {
  const url = routes.CREATE_BLOG_POST;
  return ApiService.request({
    url,
    http_method: "POST",
    data,
  });
};

export const updateBlogPost = (
  id: string,
  data: UpdateBlogPostCommand
): Promise<BlogPostUpdated> => {
  const url = `${routes.UPDATE_BLOG_POST}/${id}`;
  return ApiService.request({
    url,
    http_method: "PUT",
    data,
  });
};

export const trashBlogPost = (id: string): Promise<BlogPostUpdated> => {
  const url = `${routes.UPDATE_BLOG_POST}/${id}`;
  return ApiService.request({
    url,
    http_method: "PATCH",
    data: { status: "trashed" },
  });
};

export const restoreBlogPost = (id: string): Promise<BlogPostUpdated> => {
  const url = `${routes.UPDATE_BLOG_POST}/${id}`;
  return ApiService.request({
    url,
    http_method: "PATCH",
    data: { status: "draft" },
  });
};

export const deleteBlogPost = (id: string): Promise<BlogPostDeleted> => {
  const url = `${routes.DELETE_BLOG_POST}/${id}`;
  return ApiService.request({
    url,
    http_method: "DELETE",
  });
};

export const BlogService = {
  getBlogCategories,
  getBlogCategoryById,
  getBlogCategoryBySlug,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
  getBlogPosts,
  getBlogPostBySlug,
  createBlogPost,
  updateBlogPost,
  trashBlogPost,
  restoreBlogPost,
  deleteBlogPost,
};
